import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useConfetti } from '../hooks/useConfetti';
import {
  Trophy, Users, Briefcase, CheckCircle, PoundSterling,
  Star, Lock, Crown, Award, TrendingUp,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LS_KEY = 'milestone_earned_ids_v1';

const fmt = (v) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
const fmtDate = (d) => { if (!d) return null; const p = d.split('T')[0].split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : null; };
const pct = (val, max) => max > 0 ? Math.min(100, Math.round((val / max) * 100)) : 0;

const ICON_MAP = { Users, Briefcase, CheckCircle, PoundSterling, Star, Crown, Award, TrendingUp };

const CATEGORY_META = {
  clients:    { label: 'Client Milestones',     color: 'blue',   bg: 'bg-blue-500',   light: 'bg-blue-100 dark:bg-blue-900/30',   text: 'text-blue-600 dark:text-blue-400' },
  cases:      { label: 'Case Milestones',        color: 'green',  bg: 'bg-green-500',  light: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-600 dark:text-green-400' },
  commission: { label: 'Commission Milestones',  color: 'amber',  bg: 'bg-amber-500',  light: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-600 dark:text-amber-400' },
  special:    { label: 'Special Badges',         color: 'red',    bg: 'bg-red-500',    light: 'bg-red-100 dark:bg-red-900/30',     text: 'text-red-600 dark:text-red-400' },
};

// ── Build static badge definitions ─────────────────────────────────────────

function buildBadgeDefinitions() {
  const defs = [];

  // CLIENT badges: 1, 5, 10, 15 … 100
  const clientThresholds = [1, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100];
  clientThresholds.forEach(n => {
    defs.push({ id: `client_${n}`, category: 'clients', icon: 'Users', target: n, isMoney: false,
      name: n === 1 ? 'First Client' : `${n} Clients`,
      description: n === 1 ? 'Added your very first client' : `Built a client book of ${n}`,
    });
  });

  // CASE — first case
  defs.push({ id: 'case_first', category: 'cases', icon: 'Briefcase', target: 1, isMoney: false,
    name: 'First Case', description: 'Submitted your first case',
  });

  // CASE — completions: 1, 5, 10, 15, 20, 30, 40, 50, 60, 70, 80, 90, 100
  const completionThresholds = [1, 5, 10, 15, 20, 30, 40, 50, 60, 70, 80, 90, 100];
  completionThresholds.forEach(n => {
    defs.push({ id: `completion_${n}`, category: 'cases', icon: 'CheckCircle', target: n, isMoney: false,
      name: n === 1 ? 'First Completion' : `${n} Completions`,
      description: n === 1 ? 'Completed your first case' : `Reached ${n} completed cases`,
    });
  });

  // COMMISSION
  const commThresholds = [
    { val: 0.01, name: 'First Commission', description: 'Received your first commission payment' },
    { val: 2000 }, { val: 4000 }, { val: 8000 }, { val: 10000 }, { val: 15000 }, { val: 20000 },
  ];
  for (let v = 25000; v <= 100000; v += 5000)  commThresholds.push({ val: v });
  for (let v = 125000; v <= 250000; v += 25000) commThresholds.push({ val: v });

  commThresholds.forEach(({ val, name, description }) => {
    defs.push({
      id: `commission_${val}`,
      category: 'commission',
      icon: val <= 20000 ? 'PoundSterling' : 'TrendingUp',
      target: val,
      isMoney: true,
      name: name || `${fmt(val)} Earned`,
      description: description || `Earned ${fmt(val)} total commission`,
    });
  });

  // SPECIAL
  defs.push({ id: 'special_mortgage_lender',  category: 'special', icon: 'Star',  target: 3, isMoney: false, name: 'Most Used Lender (Mortgage)',  description: 'Use a single mortgage lender for 3+ cases' });
  defs.push({ id: 'special_insurance_lender', category: 'special', icon: 'Star',  target: 3, isMoney: false, name: 'Most Used Lender (Insurance)', description: 'Use a single insurance provider for 3+ cases' });
  defs.push({ id: 'special_loyal_client',     category: 'special', icon: 'Crown', target: 3, isMoney: false, name: 'Loyal Client',                  description: 'A client returns for 3 or more cases' });

  return defs;
}

const BADGE_DEFS = buildBadgeDefinitions();

// ── Component ───────────────────────────────────────────────────────────────

const Milestones = () => {
  const [clients, setClients] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const fireConfetti = useConfetti();

  const authHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
  }), []);

  useEffect(() => {
    const load = async () => {
      try {
        const [cr, csr] = await Promise.all([
          fetch(`${API_URL}/api/clients?limit=2000`, { headers: authHeaders() }),
          fetch(`${API_URL}/api/cases?limit=2000`,   { headers: authHeaders() }),
        ]);
        const cd = cr.ok  ? await cr.json()  : {};
        const csd = csr.ok ? await csr.json() : {};
        setClients(cd.clients  || []);
        setCases(csd.cases || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [authHeaders]);

  // ── Derived data ──────────────────────────────────────────────────────────

  // For each client, find their earliest application submission date from cases
  const clientFirstAppDate = useMemo(() => {
    const map = {};
    cases.forEach(c => {
      if (c.client_id && c.date_application_submitted) {
        const existing = map[c.client_id];
        const d = new Date(c.date_application_submitted);
        if (!existing || d < existing.date) map[c.client_id] = { date: d, raw: c.date_application_submitted };
      }
    });
    return map;
  }, [cases]);

  // Clients sorted by their first application submission date (fallback to created_at)
  const sortedClients = useMemo(() =>
    [...clients].sort((a, b) => {
      const da = clientFirstAppDate[a.client_id]?.date || new Date(a.created_at || 0);
      const db = clientFirstAppDate[b.client_id]?.date || new Date(b.created_at || 0);
      return da - db;
    }),
  [clients, clientFirstAppDate]);

  const completedCases = useMemo(() => cases.filter(c => c.status === 'completed'), [cases]);

  const paidCases = useMemo(() =>
    cases
      .filter(c => c.commission_status === 'paid' && c.commission_paid_date)
      .sort((a, b) => new Date(a.commission_paid_date) - new Date(b.commission_paid_date)),
  [cases]);

  const totalCommission = useMemo(() =>
    paidCases.reduce((s, c) => s + (c.gross_commission || 0) + (c.client_fee || 0), 0),
  [paidCases]);

  const commissionByDate = useMemo(() => {
    let running = 0;
    return paidCases.map(c => {
      running += (c.gross_commission || 0) + (c.client_fee || 0);
      return { date: c.commission_paid_date, total: running };
    });
  }, [paidCases]);

  const mortgageLenderCounts = useMemo(() => {
    const counts = {};
    cases.filter(c => c.product_type === 'mortgage' && c.lender_name)
         .forEach(c => { counts[c.lender_name] = (counts[c.lender_name] || 0) + 1; });
    return counts;
  }, [cases]);

  const insuranceLenderCounts = useMemo(() => {
    const counts = {};
    cases.filter(c => c.product_type === 'insurance' && c.lender_name)
         .forEach(c => { counts[c.lender_name] = (counts[c.lender_name] || 0) + 1; });
    return counts;
  }, [cases]);

  const mostUsedMortgageLender = useMemo(() => {
    const entries = Object.entries(mortgageLenderCounts);
    if (!entries.length) return null;
    const top = entries.sort((a, b) => b[1] - a[1])[0];
    return top[1] >= 3 ? top : null;
  }, [mortgageLenderCounts]);

  const mostUsedInsuranceLender = useMemo(() => {
    const entries = Object.entries(insuranceLenderCounts);
    if (!entries.length) return null;
    const top = entries.sort((a, b) => b[1] - a[1])[0];
    return top[1] >= 3 ? top : null;
  }, [insuranceLenderCounts]);

  const clientCaseCounts = useMemo(() => {
    const counts = {};
    cases.forEach(c => { if (c.client_id) counts[c.client_id] = (counts[c.client_id] || 0) + 1; });
    return counts;
  }, [cases]);

  const loyalClient = useMemo(() => {
    const loyalId = Object.entries(clientCaseCounts).find(([, v]) => v >= 3)?.[0];
    if (!loyalId) return null;
    const c = clients.find(x => x.client_id === loyalId);
    return c ? `${c.first_name} ${c.last_name}` : 'A client';
  }, [clientCaseCounts, clients]);

  const maxClientCases = useMemo(() => {
    const vals = Object.values(clientCaseCounts);
    return vals.length ? Math.max(...vals) : 0;
  }, [clientCaseCounts]);

  // ── Evaluate badges ───────────────────────────────────────────────────────

  const badges = useMemo(() => {
    const clientCount = clients.length;
    const caseCount   = cases.length;
    const compCount   = completedCases.length;

    const commDateFor = (threshold) => {
      const entry = commissionByDate.find(e => e.total >= threshold);
      return entry ? fmtDate(entry.date) : null;
    };

    const topMortgageCount = Object.keys(mortgageLenderCounts).length
      ? Math.max(...Object.values(mortgageLenderCounts)) : 0;
    const topInsuranceCount = Object.keys(insuranceLenderCounts).length
      ? Math.max(...Object.values(insuranceLenderCounts)) : 0;

    return BADGE_DEFS.map(def => {
      let earned = false;
      let progress = 0;
      let dateEarned = null;
      let dynamicName = def.name;
      let dynamicDesc = def.description;

      // ── client badges ──
      if (def.id.startsWith('client_')) {
        const n = def.target;
        earned = clientCount >= n;
        progress = Math.min(clientCount, n);
        if (earned && sortedClients[n - 1]) {
          const nth = sortedClients[n - 1];
          const appDate = clientFirstAppDate[nth.client_id]?.raw;
          dateEarned = fmtDate(appDate || nth.created_at);
        }
      }

      // ── first case ──
      else if (def.id === 'case_first') {
        const submitted = cases.filter(c => c.date_application_submitted);
        earned = submitted.length >= 1;
        progress = Math.min(submitted.length, 1);
        if (earned) {
          const first = submitted.sort((a, b) => new Date(a.date_application_submitted) - new Date(b.date_application_submitted))[0];
          dateEarned = fmtDate(first.date_application_submitted);
        }
      }

      // ── completion badges ──
      else if (def.id.startsWith('completion_')) {
        const n = def.target;
        earned = compCount >= n;
        progress = Math.min(compCount, n);
      }

      // ── commission badges ──
      else if (def.id.startsWith('commission_')) {
        earned = totalCommission >= def.target;
        progress = Math.min(totalCommission, def.target);
        if (earned) dateEarned = commDateFor(def.target);
      }

      // ── special badges ──
      else if (def.id === 'special_mortgage_lender') {
        earned = !!mostUsedMortgageLender;
        progress = Math.min(topMortgageCount, 3);
        if (earned) {
          dynamicName = mostUsedMortgageLender[0];
          dynamicDesc = `${mostUsedMortgageLender[0]} is your most used mortgage lender (${mostUsedMortgageLender[1]} cases)`;
        }
      }
      else if (def.id === 'special_insurance_lender') {
        earned = !!mostUsedInsuranceLender;
        progress = Math.min(topInsuranceCount, 3);
        if (earned) {
          dynamicName = mostUsedInsuranceLender[0];
          dynamicDesc = `${mostUsedInsuranceLender[0]} is your most used insurance provider (${mostUsedInsuranceLender[1]} cases)`;
        }
      }
      else if (def.id === 'special_loyal_client') {
        earned = !!loyalClient;
        progress = Math.min(maxClientCases, 3);
        if (earned) {
          dynamicName = loyalClient;
          dynamicDesc = `${loyalClient} has come back for 3+ cases`;
        }
      }

      return { ...def, earned, progress, dateEarned, name: dynamicName, description: dynamicDesc };
    });
  }, [
    clients, cases, completedCases, sortedClients, clientFirstAppDate,
    totalCommission, commissionByDate,
    mostUsedMortgageLender, mostUsedInsuranceLender, loyalClient,
    mortgageLenderCounts, insuranceLenderCounts, maxClientCases,
  ]);

  // ── Confetti for new badges ───────────────────────────────────────────────

  useEffect(() => {
    if (loading || !badges.length) return;
    const earnedIds = badges.filter(b => b.earned).map(b => b.id);
    const prevIds = JSON.parse(localStorage.getItem(LS_KEY) || '[]');
    const newOnes = earnedIds.filter(id => !prevIds.includes(id));
    if (newOnes.length > 0) fireConfetti();
    localStorage.setItem(LS_KEY, JSON.stringify(earnedIds));
  }, [loading, badges, fireConfetti]);

  // ── Render ────────────────────────────────────────────────────────────────

  const earnedCount = badges.filter(b => b.earned).length;
  const totalCount  = badges.length;

  const BadgeCard = ({ badge }) => {
    const Icon = ICON_MAP[badge.icon] || Trophy;
    const meta = CATEGORY_META[badge.category];
    const progressPct = badge.isMoney
      ? pct(badge.progress, badge.target)
      : pct(badge.progress, badge.target);

    if (badge.earned) {
      return (
        <div className="relative rounded-xl border border-amber-300 dark:border-amber-700 bg-gradient-to-br from-amber-50 to-white dark:from-amber-900/20 dark:to-slate-800 p-4 shadow-md shadow-amber-200/60 dark:shadow-amber-900/40 transition-all hover:scale-[1.02]">
          {/* Earned tick */}
          <div className="absolute top-3 right-3 h-5 w-5 rounded-full bg-green-500 flex items-center justify-center">
            <CheckCircle className="h-3.5 w-3.5 text-white" />
          </div>

          {/* Icon */}
          <div className={`h-12 w-12 rounded-full ${meta.light} flex items-center justify-center mb-3`}>
            <Icon className={`h-6 w-6 ${meta.text}`} />
          </div>

          {/* Category label */}
          {badge.category === 'special' && (
            <p className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-0.5">{badge.description.includes('mortgage') || badge.id === 'special_mortgage_lender' ? 'Most Used Lender (Mortgage)' : badge.id === 'special_insurance_lender' ? 'Most Used Lender (Insurance)' : 'Loyal Client'}</p>
          )}

          <p className="font-semibold text-slate-900 dark:text-white text-sm leading-tight mb-1">{badge.name}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 leading-snug">{badge.description}</p>

          {badge.dateEarned && (
            <p className="text-[10px] text-amber-600 dark:text-amber-400 font-medium mt-2">Earned {badge.dateEarned}</p>
          )}
          {!badge.dateEarned && (
            <p className="text-[10px] text-green-600 dark:text-green-400 font-medium mt-2">Earned ✓</p>
          )}
        </div>
      );
    }

    // Unearned
    return (
      <div className="relative rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 opacity-70 hover:opacity-90 transition-opacity">
        {/* Lock */}
        <div className="absolute top-3 right-3">
          <Lock className="h-3.5 w-3.5 text-slate-400" />
        </div>

        {/* Icon — greyed */}
        <div className="h-12 w-12 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center mb-3">
          <Icon className="h-6 w-6 text-slate-400 dark:text-slate-500" />
        </div>

        <p className="font-semibold text-slate-600 dark:text-slate-300 text-sm leading-tight mb-1">{badge.name}</p>
        <p className="text-xs text-slate-400 dark:text-slate-500 leading-snug mb-3">{badge.description}</p>

        {/* Progress bar */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] text-slate-400 dark:text-slate-500">
              {badge.isMoney ? `${fmt(badge.progress)} / ${fmt(badge.target)}` : `${badge.progress} / ${badge.target}`}
            </span>
            <span className="text-[10px] text-slate-400 dark:text-slate-500">{progressPct}%</span>
          </div>
          <div className="h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
            <div
              className={`h-full ${meta.bg} rounded-full transition-all duration-500`}
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      </div>
    );
  };

  const Section = ({ category }) => {
    const meta = CATEGORY_META[category];
    const sectionBadges = badges.filter(b => b.category === category);
    const sectionEarned = sectionBadges.filter(b => b.earned).length;
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className={`h-8 w-8 rounded-full ${meta.light} flex items-center justify-center`}>
            <Trophy className={`h-4 w-4 ${meta.text}`} />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            {meta.label}
          </h2>
          <span className="text-sm text-slate-400 dark:text-slate-500">
            {sectionEarned}/{sectionBadges.length}
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {sectionBadges.map(b => <BadgeCard key={b.id} badge={b} />)}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin h-8 w-8 border-4 border-red-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  const overallPct = pct(earnedCount, totalCount);

  return (
    <div className="p-6 space-y-10 animate-fadeIn">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Plus Jakarta Sans' }}>
          Milestones
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">Track your achievements as your business grows.</p>
      </div>

      {/* Summary card */}
      <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-gradient-to-br from-amber-50 to-white dark:from-amber-900/10 dark:to-slate-800 p-6 flex flex-col sm:flex-row items-start sm:items-center gap-6">
        <div className="h-16 w-16 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center shrink-0">
          <Trophy className="h-8 w-8 text-amber-600 dark:text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            {earnedCount} <span className="text-lg text-slate-500 dark:text-slate-400 font-normal">/ {totalCount} badges earned</span>
          </p>
          <div className="mt-3 h-3 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden max-w-md">
            <div
              className="h-full bg-gradient-to-r from-amber-400 to-amber-600 rounded-full transition-all duration-700"
              style={{ width: `${overallPct}%` }}
            />
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{overallPct}% complete</p>
        </div>
      </div>

      {/* Sections */}
      {['clients', 'cases', 'commission', 'special'].map(cat => (
        <Section key={cat} category={cat} />
      ))}
    </div>
  );
};

export default Milestones;
